/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Input, message, Radio } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { convertToTender, createFolder, createTender } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import IconButton from '~components/_common/IconButton/IconButton';
import { getPath } from '~src/utils/getFilePath';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useProject } from '~src/hooks/api-hooks/useProject';

export interface CreateFolderModalProps extends Partial<ModalProps> {
  parent?: DbFolder;
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  parent,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();

  // Project + permissions
  const { data: projectData } = useProject(true, true, {
    enabled: !!projectId,
  });

  // Only Project Admin and higher can create Tender folders
  const canCreateTenderFolder = !!(
    projectData?.is_superuser ||
    projectData?.is_tender_admin ||
    projectData?.is_permission_manager
  );

  const [folderType, setFolderType] = useState<'archive' | 'tender'>('archive');
  const [nameVal, setNameVal] = useState('');
  const [descriptionVal, setDescriptionVal] = useState('');
  const nameInputRef = useRef<Input>(null);
  const archiveSettings = useArchiveSettings();
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on;

  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );

  const [doConvertToTender] = useMutation(convertToTender, {
    onSuccess: () => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      queryCache.invalidateQueries('folders');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      appLog('ConvertToTender -> convertTender -> item', item);
      appLog('ConvertToTender -> convertTender -> err', error);
    },
  });

  const [doCreateFolder, { isLoading }] = useMutation(createFolder, {
    onSuccess: (theFolder) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      // reset values
      setNameVal('');
      setDescriptionVal('');

      // decide if this folder should be converted to Tender folder
      // Only user choice (folderType === 'tender') + permission.
      const shouldCreateTenderFolder =
        canCreateTenderFolder && folderType === 'tender';

      if (shouldCreateTenderFolder) {
        doConvertToTender({
          projectId: String(projectId),
          folderId: theFolder.id,
        });
      } else {
        queryCache.invalidateQueries('folders');
      }
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-create-failed' }));
      appLog('CreateFolderModal -> createFolder -> item', item);
      appLog('CreateFolderModal -> createFolder -> err', error);
    },
  });

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);

      // initial folder type when opening modal
      // Always default to ARCHIVE
      const initialFolderType: 'archive' | 'tender' = 'archive';
      setFolderType(initialFolderType);

      // fetch folders if data is older than 1min
      if (Date.now() - foldersQuery.updatedAt > 60 * 1000) {
        foldersQuery.refetch();
      }
    }
  }, [modalProps.visible, foldersQuery.updatedAt]);

  const isValid = () => {
    if (!nameVal) return false;

    // check if folder with the same name exists
    if (foldersQuery.data?.length) {
      const parentId = parent?.id || null;
      const trimmedName = nameVal.trim();

      const exists = foldersQuery.data.some(
        (v) => v.parent_id === parentId && v.name.trim() === trimmedName
      );

      if (exists) return false;
    }

    return true;
  };

  const shouldCreateTenderFolder =
    canCreateTenderFolder && folderType === 'tender';

  const isTenderFolder = shouldCreateTenderFolder;

  const handleSubmit = async () => {
    if (!isValid()) return;

    // Permission check: user is trying to create Tender folder without rights
    if (folderType === 'tender' && !canCreateTenderFolder) {
      message.error(
        intl.formatMessage({ id: 'tender-folder-no-permission' }) ||
          'You do not have permission to create Tender folders.'
      );
      return;
    }

    doCreateFolder({
      projectId: String(projectId),
      folderData: {
        parent_id: parent ? parent.id : null,
        name: nameVal,
        description: descriptionVal,
        is_locked: false,
        state: DB_STATE.AVAILABLE,
      },
    });
  };

  const isParentTenderFolder =
    parent?.option?.is_tender_folder === true ||
    (parent as any)?.option?.is_tender_root_folder === true;

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'create-folder' })}
          icon={['far', 'folder-plus']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      {isTenderFolder && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          {intl.formatMessage({
            id: 'tender-folder-warning',
          })}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr 0.5fr',
          gap: '4px',
          paddingBottom: '4px',
        }}>
        <h4 style={{ marginRight: '5px' }}>
          {intl.formatMessage({ id: 'path' })}
        </h4>
        <h4>{parent ? getPath(foldersQuery.data, parent?.id) : '/'}</h4>

        <IconButton
          icon={['far', 'info-circle']}
          customStyle={{ fontSize: '130%' }}
          containerStyle={{
            marginLeft: 0,
            color: '#7a829b',
          }}
          backgroundOff
          tooltip={
            intl.locale === 'da' ? (
              <div>Opret ny mappe i stien</div>
            ) : (
              <div>Create new folder</div>
            )
          }
        />
      </div>
      {isTenderingOn && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 0.5fr',
            alignItems: 'center',
            paddingBottom: '14px',
          }}>
          <h4 style={{ marginRight: '10%' }}>
            {intl.formatMessage({ id: 'folder-type' })}
          </h4>
          <Radio.Group
            value={folderType}
            onChange={(e) => setFolderType(e.target.value)}
            // Inside Tender folders we disable creating Tender again
            // to avoid backend 400 on convertToTender.
            disabled={isParentTenderFolder}
            buttonStyle="solid">
            <Radio.Button value="archive">
              {intl.formatMessage({ id: 'archive' })}
            </Radio.Button>
            <Radio.Button value="tender">
              {intl.formatMessage({ id: 'tender' })}
            </Radio.Button>
          </Radio.Group>
        </div>
      )}
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'name' })}
        allowClear
        autoCapitalize="words"
        value={nameVal}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        onChange={({ target: { value } }) => setNameVal(value)}
      />
      <Input.TextArea
        css={{
          marginTop: 10,
        }}
        placeholder={intl.formatMessage({ id: 'description' })}
        value={descriptionVal}
        onChange={({ target: { value } }) => setDescriptionVal(value)}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
